import { useEffect, useRef } from 'react';
import axios from 'axios';
import { APP_VERSION } from '../version';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CHECK_INTERVAL_MS = 60 * 1000; // 1 minute
const RELOAD_FLAG_KEY = 'ticketasaurus_reload_at';
const RELOAD_COOLDOWN_MS = 5 * 60 * 1000; // don't hot-loop reload within 5 minutes

/**
 * Poll the backend version endpoint every minute + on window focus.
 * If the deployed version doesn't match the bundled APP_VERSION, the client
 * has stale JS (common on iOS Safari / PWAs with cached service workers).
 * Force a hard reload — bypassing HTTP cache — so the user sees the new build.
 */
export function useVersionCheck() {
  const reloading = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const doReload = async () => {
      if (reloading.current) return;
      const last = parseInt(localStorage.getItem(RELOAD_FLAG_KEY) || '0', 10);
      if (Date.now() - last < RELOAD_COOLDOWN_MS) return; // avoid loops
      localStorage.setItem(RELOAD_FLAG_KEY, String(Date.now()));
      reloading.current = true;
      // iOS PWAs + Chrome may serve a cached bundle.js from a service worker even after reload.
      // Unregister every SW and clear all Cache Storage entries before reloading.
      try {
        if ('serviceWorker' in navigator) {
          const regs = await navigator.serviceWorker.getRegistrations();
          await Promise.all(regs.map(r => r.unregister()));
        }
        if (window.caches && caches.keys) {
          const keys = await caches.keys();
          await Promise.all(keys.map(k => caches.delete(k)));
        }
      } catch (e) { /* best effort */ }
      // Cache-bust every asset via query-string suffix, then hard reload
      window.location.reload(true);
    };

    const check = async () => {
      try {
        // add cache-buster so proxies / SWs can't serve a stale response
        const r = await axios.get(`${BACKEND_URL}/api/version?t=${Date.now()}`, {
          headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
          timeout: 8000,
        });
        const serverVersion = r?.data?.version;
        if (!cancelled && serverVersion && serverVersion !== APP_VERSION) {
          console.warn(`[version-check] client=${APP_VERSION} server=${serverVersion} — reloading`);
          doReload();
        }
      } catch (e) {
        // silent — network hiccups shouldn't break the app
      }
    };

    // Kick off immediately, then on interval + when tab regains focus
    check();
    const id = setInterval(check, CHECK_INTERVAL_MS);
    const onFocus = () => check();
    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onFocus);
    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('online', onFocus);
    };
  }, []);
}

export default useVersionCheck;
